/**
 * Settings → Retention (design-reference §4.11).
 *
 * Retention policy controls (originals / derived / grace) read from and written
 * to GET/PATCH /v1/retention, the cleanup-history table (GET
 * /v1/retention/history), and a GDPR erasure action (POST /v1/retention/erasure)
 * behind a double-confirmation (a destructive dialog gated on typing ERASE). The
 * nightly cleanup schedule is a real system fact (02:00 UTC); no counts are
 * invented — every number shown comes from an API field.
 */
import { useCallback, useEffect, useState } from 'react';

import type { RetentionHistoryEntry, TenantRetention } from '../../api/schema';
import { Button, ConfirmModal, ErrorState, Skeleton, useToast } from '../../components';
import { useLoadState } from '../../lib/useLoadState';
import { useSettingsApi } from './settingsApi';
import { fmtDate } from './format';
import './settings.css';

const YEAR_OPTIONS: readonly { label: string; days: number }[] = [
  { label: '1 year', days: 365 },
  { label: '3 years', days: 1095 },
  { label: '7 years', days: 2555 },
  { label: '10 years', days: 3650 },
];

interface RetentionData {
  policy: TenantRetention;
  history: RetentionHistoryEntry[];
}

const HEX24 = /^[0-9a-fA-F]{24}$/;

function historyNote(deleted: number): string {
  return deleted === 0 ? 'nothing expired' : 'nightly';
}

function Segmented({
  label,
  value,
  onChange,
}: {
  label: string;
  value: number;
  onChange: (days: number) => void;
}): React.JSX.Element {
  return (
    <div className="set-policy-row">
      <span className="set-label">{label}</span>
      <div className="set-seg" role="group" aria-label={label}>
        {YEAR_OPTIONS.map((opt) => {
          const on = opt.days === value;
          return (
            <button
              key={opt.days}
              type="button"
              className={on ? 'set-seg__btn set-seg__btn--on' : 'set-seg__btn'}
              aria-pressed={on}
              onClick={() => { onChange(opt.days); }}
            >
              {opt.label}
            </button>
          );
        })}
      </div>
    </div>
  );
}

export function RetentionScreen(): React.JSX.Element {
  const { toast } = useToast();
  const settingsApi = useSettingsApi();

  const [originals, setOriginals] = useState(3650);
  const [derived, setDerived] = useState(3650);
  const [grace, setGrace] = useState(7);
  const [saving, setSaving] = useState(false);

  const [erasureTarget, setErasureTarget] = useState('');
  const [erasureConfirm, setErasureConfirm] = useState(false);
  const [erasing, setErasing] = useState(false);

  const fetchRetention = useCallback(
    async (signal: AbortSignal): Promise<RetentionData> => {
      const [policy, history] = await Promise.all([
        settingsApi.retention.get(signal),
        settingsApi.retention.history(signal),
      ]);
      return { policy, history };
    },
    [settingsApi],
  );
  const state = useLoadState(fetchRetention);
  const reload = state.status === 'ready' || state.status === 'error' ? state.reload : undefined;

  // Re-seed the editable policy fields whenever a fresh policy loads — first
  // load, or a reload after a save.
  useEffect(() => {
    if (state.status !== 'ready') return;
    setOriginals(state.data.policy.originals_days ?? 3650);
    setDerived(state.data.policy.derived_days ?? 3650);
    setGrace(state.data.policy.grace_days ?? 7);
  }, [state]);

  const save = useCallback(() => {
    setSaving(true);
    void (async () => {
      try {
        await settingsApi.retention.update({
          originals_days: originals,
          derived_days: derived,
          grace_days: grace,
        });
        toast('Retention saved — next cleanup tonight 02:00 UTC will honour the new policy');
        reload?.();
      } catch {
        toast('Could not save the policy — try again', { tone: 'error' });
      } finally {
        setSaving(false);
      }
    })();
  }, [originals, derived, grace, toast, reload, settingsApi]);

  const runErasure = useCallback(() => {
    const target = erasureTarget.trim();
    if (target === '') return;
    setErasing(true);
    void (async () => {
      try {
        const result = await settingsApi.retention.erase({
          confirm: true,
          acknowledge: 'ERASE',
          ...(HEX24.test(target)
            ? { document_id: target }
            : { selector: { key: 'reference', value: target } }),
        });
        toast(
          `Erasure queued (${String(result.erased_count)} document${result.erased_count === 1 ? '' : 's'}) — irreversible after the 7-day grace window; audit entry written`,
        );
        setErasureConfirm(false);
        setErasureTarget('');
      } catch {
        toast('Could not queue the erasure — try again', { tone: 'error' });
      } finally {
        setErasing(false);
      }
    })();
  }, [erasureTarget, toast, settingsApi]);

  if (state.status === 'loading') {
    return (
      <section className="set-wrap" aria-label="Retention">
        <div className="set-two set-two--1-1">
          <Skeleton height={320} />
          <Skeleton height={320} />
        </div>
        <p className="set-loading-caption">Loading retention policy…</p>
      </section>
    );
  }

  if (state.status === 'error') {
    return (
      <section className="set-wrap" aria-label="Retention">
        <ErrorState
          title="We couldn't load your retention policy"
          body="The settings service didn't respond. Your data is untouched — this only affects this view."
          traceId={state.traceId ?? undefined}
          actions={
            <Button onClick={state.reload} variant="primary">
              Retry
            </Button>
          }
        />
      </section>
    );
  }

  const { history } = state.data;

  return (
    <section className="set-wrap" aria-label="Retention">
      <div className="set-two set-two--1-1">
        {/* Left: policy ------------------------------------------------------ */}
        <div className="set-card">
          <h2 className="set-card__title">Retention policy</h2>
          <Segmented label="Original files (Blob)" value={originals} onChange={setOriginals} />
          <Segmented
            label="Derived data (OCR text, classifications, analytics)"
            value={derived}
            onChange={setDerived}
          />
          <div className="set-inline">
            <span className="set-label">Grace window before hard delete</span>
            <input
              className="set-numeric"
              type="number"
              min={0}
              aria-label="Grace window in days"
              value={grace}
              onChange={(e) => { setGrace(Math.max(0, Number(e.currentTarget.value))); }}
            />
            <span className="set-hint">days (soft-delete, recoverable)</span>
          </div>
          <div className="set-warnblock">
            Cleanup deletes the Blob original <strong>and</strong> the database record, and writes an audit entry.
            After the grace window this is irreversible.
          </div>
          <div className="set-actions-row">
            <Button variant="primary" loading={saving} onClick={save}>
              Save policy
            </Button>
            <span className="set-hint">Next scheduled cleanup: tonight 02:00 UTC</span>
          </div>
        </div>

        {/* Right column ------------------------------------------------------ */}
        <div className="set-col">
          <div className="set-card">
            <h2 className="set-card__title">Cleanup history</h2>
            {history.length === 0 ? (
              <p className="set-hint">No cleanups have run yet.</p>
            ) : (
              <div className="set-scroll">
                <div
                  className="set-grid set-grid__head"
                  role="row"
                  style={{ gridTemplateColumns: '1.2fr 0.8fr 1fr 70px' }}
                >
                  <span role="columnheader">Date</span>
                  <span role="columnheader">Records</span>
                  <span role="columnheader">Note</span>
                  <span role="columnheader" className="sr-only">
                    Audit
                  </span>
                </div>
                <ul style={{ listStyle: 'none', margin: 0, padding: 0 }} aria-label="Cleanup history">
                  {history.map((entry) => (
                    <li
                      className="set-grid set-grid__row"
                      key={entry.date}
                      style={{ gridTemplateColumns: '1.2fr 0.8fr 1fr 70px' }}
                    >
                      <span>{fmtDate(entry.date)}</span>
                      <span className="set-mono">{entry.deleted}</span>
                      <span style={{ color: 'var(--t3)' }}>{historyNote(entry.deleted)}</span>
                      <span style={{ textAlign: 'right' }}>
                        <button
                          type="button"
                          className="set-linkbtn"
                          onClick={() => { toast('Audit entries open in the audit log'); }}
                        >
                          Audit →
                        </button>
                      </span>
                    </li>
                  ))}
                </ul>
              </div>
            )}
          </div>

          <div className="set-card">
            <h2 className="set-card__title">GDPR erasure request</h2>
            <p className="set-label" style={{ margin: 0 }}>
              Target a document or data subject for deletion ahead of policy — double-confirmed and fully audited.
            </p>
            <div className="set-inline">
              <input
                className="ds-input"
                style={{ flex: 1 }}
                placeholder="Filename, document id, or subject reference…"
                aria-label="Erasure target"
                value={erasureTarget}
                onChange={(e) => { setErasureTarget(e.currentTarget.value); }}
              />
              <button
                type="button"
                className="set-danger-btn"
                disabled={erasureTarget.trim() === ''}
                onClick={() => { setErasureConfirm(true); }}
              >
                Request erasure
              </button>
            </div>
          </div>
        </div>
      </div>

      <ConfirmModal
        open={erasureConfirm}
        onCancel={() => { setErasureConfirm(false); }}
        onConfirm={runErasure}
        title="Confirm GDPR erasure"
        destructive
        acknowledgeWord="ERASE"
        confirmLabel="Erase permanently"
        loading={erasing}
      >
        Everything matching <strong>{erasureTarget}</strong> — originals, OCR text, classifications and analytics
        contributions — will be deleted after the 7-day grace window. An audit entry records the request; the data
        itself is unrecoverable.
      </ConfirmModal>
    </section>
  );
}
